import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  Car, Bike, Truck, Search, Calendar, Fuel, 
  Gauge, Settings, MapPin, Phone, ChevronRight,
  Eye, Heart, Share2, Filter
} from 'lucide-react';

const ANNONCES = [
  // Voitures
  {
    id: 'ann-1',
    category: 'auto',
    title: 'Peugeot 308 GT Line',
    year: 2021,
    km: 45000,
    fuel: 'Diesel',
    transmission: 'Automatique',
    price: 22500,
    description: 'Très bon état, entretien complet chez Peugeot. GPS, caméra de recul, toit panoramique.',
    image: 'https://images.unsplash.com/photo-1609521263047-f8f205293f24?w=600',
    features: ['GPS', 'Caméra recul', 'Toit panoramique', 'Sièges chauffants'],
    certified: true,
    views: 234
  },
  {
    id: 'ann-2',
    category: 'auto',
    title: 'BMW Série 3 320d',
    year: 2020,
    km: 62000,
    fuel: 'Diesel',
    transmission: 'Automatique',
    price: 28900,
    description: 'Pack M Sport, intérieur cuir, carnet d\'entretien complet. Véhicule non fumeur.',
    image: 'https://images.unsplash.com/photo-1555215695-3004980ad54e?w=600',
    features: ['Pack M Sport', 'Cuir', 'LED', 'Régulateur adaptatif'],
    certified: true,
    views: 456
  },
  {
    id: 'ann-3',
    category: 'auto',
    title: 'Renault Clio V',
    year: 2022,
    km: 28000,
    fuel: 'Essence',
    transmission: 'Manuelle',
    price: 15900,
    description: 'Première main, garantie constructeur jusqu\'en 2025. Idéale pour la ville.',
    image: 'https://images.unsplash.com/photo-1619682817481-e994891cd1f5?w=600',
    features: ['Apple CarPlay', 'Aide au stationnement', 'Climatisation auto'],
    certified: true,
    views: 189
  },
  {
    id: 'ann-4',
    category: 'auto',
    title: 'Mercedes Classe A 180',
    year: 2021,
    km: 38000,
    fuel: 'Essence',
    transmission: 'Automatique',
    price: 26500,
    description: 'Finition AMG Line, écran MBUX, système audio premium.',
    image: 'https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?w=600',
    features: ['AMG Line', 'MBUX', 'LED', 'Keyless'],
    certified: true,
    views: 312
  },
  // Motos & Scooters
  {
    id: 'ann-5',
    category: 'moto',
    title: 'Yamaha MT-07',
    year: 2022,
    km: 8500,
    fuel: 'Essence',
    transmission: 'Manuelle',
    price: 6900,
    description: 'Excellent état, révision récente. Pot Akrapovic, protections.',
    image: 'https://images.unsplash.com/photo-1558981806-ec527fa84c39?w=600',
    features: ['Pot Akrapovic', 'Protections', 'Béquille centrale'],
    certified: false,
    views: 156
  },
  {
    id: 'ann-6',
    category: 'moto',
    title: 'Honda PCX 125',
    year: 2023,
    km: 3200,
    fuel: 'Essence',
    transmission: 'Automatique',
    price: 3500,
    description: 'Scooter comme neuf, idéal pour les trajets urbains. Top case inclus.',
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600',
    features: ['Top case', 'Tablier', 'Alarme'],
    certified: true,
    views: 98
  },
  {
    id: 'ann-7',
    category: 'moto',
    title: 'Kawasaki Z900',
    year: 2021,
    km: 12000,
    fuel: 'Essence',
    transmission: 'Manuelle',
    price: 8500,
    description: 'Version A2 débridable, entretien à jour, pneus neufs.',
    image: 'https://images.unsplash.com/photo-1568772585407-9361f9bf3a87?w=600',
    features: ['ABS', 'Modes de conduite', 'Quickshifter'],
    certified: false,
    views: 203
  },
  // Utilitaires
  {
    id: 'ann-8',
    category: 'utilitaire',
    title: 'Renault Trafic L2H1',
    year: 2020,
    km: 85000,
    fuel: 'Diesel',
    transmission: 'Manuelle',
    price: 18500,
    description: 'Fourgon 6m³, très bien entretenu. Idéal artisan ou déménagement.',
    image: 'https://images.unsplash.com/photo-1619642751034-765dfdf7c58e?w=600',
    features: ['Bluetooth', 'Régulateur', 'Radar de recul'],
    certified: true,
    views: 87
  },
];

const CATEGORIES = [
  { id: 'all', name: 'Toutes', icon: Settings },
  { id: 'auto', name: 'Voitures', icon: Car },
  { id: 'moto', name: 'Motos & Scooters', icon: Bike },
  { id: 'utilitaire', name: 'Utilitaires', icon: Truck },
];

export default function AnnoncesPage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [priceRange, setPriceRange] = useState('all');
  
  const filteredAnnonces = ANNONCES.filter(annonce => {
    const matchCategory = activeCategory === 'all' || annonce.category === activeCategory;
    const matchSearch = annonce.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       annonce.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    let matchPrice = true;
    if (priceRange === 'under10') matchPrice = annonce.price < 10000;
    else if (priceRange === '10to20') matchPrice = annonce.price >= 10000 && annonce.price < 20000;
    else if (priceRange === '20to30') matchPrice = annonce.price >= 20000 && annonce.price < 30000;
    else if (priceRange === 'over30') matchPrice = annonce.price >= 30000;
    
    return matchCategory && matchSearch && matchPrice;
  });

  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Hero */}
      <section className="bg-gradient-to-b from-zinc-900 to-[#09090B] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-green-600/20 text-green-400 px-4 py-2 rounded-full border border-green-600/30 mb-6">
            <Car size={18} />
            <span className="text-sm font-medium">Véhicules d'occasion</span>
          </div>
          <h1 className="text-5xl font-bold text-white mb-4">
            NOS <span className="text-red-500">ANNONCES</span>
          </h1>
          <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
            Découvrez notre sélection de véhicules d'occasion révisés et garantis par nos experts
          </p>
        </div>
      </section>

      {/* Search & Filters */}
      <section className="border-b border-zinc-800 sticky top-20 z-30 bg-[#09090B]/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            {/* Search */}
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-500" size={18} />
              <Input
                type="text"
                placeholder="Rechercher un véhicule..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-zinc-800 border-zinc-700 text-white w-full"
                data-testid="search-annonces"
              />
            </div>
            
            {/* Category Filters */}
            <div className="flex gap-2 overflow-x-auto">
              {CATEGORIES.map((cat) => (
                <Button
                  key={cat.id}
                  variant={activeCategory === cat.id ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setActiveCategory(cat.id)}
                  className={activeCategory === cat.id 
                    ? 'bg-red-600 text-white' 
                    : 'text-zinc-400 hover:text-white'
                  }
                  data-testid={`filter-annonce-${cat.id}`}
                >
                  <cat.icon size={16} className="mr-2" />
                  {cat.name}
                </Button>
              ))}
            </div>

            {/* Price Filter */}
            <select
              value={priceRange}
              onChange={(e) => setPriceRange(e.target.value)}
              className="bg-zinc-800 border border-zinc-700 text-white rounded-md px-3 py-2 text-sm"
              data-testid="filter-price"
            >
              <option value="all">Tous les prix</option>
              <option value="under10">Moins de 10 000€</option>
              <option value="10to20">10 000€ - 20 000€</option>
              <option value="20to30">20 000€ - 30 000€</option>
              <option value="over30">Plus de 30 000€</option>
            </select>
          </div>
        </div>
      </section>

      {/* Results Count */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <p className="text-zinc-400">
          <span className="text-white font-bold">{filteredAnnonces.length}</span> véhicule(s) trouvé(s)
        </p>
      </div>

      {/* Annonces Grid */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAnnonces.map((annonce, idx) => (
              <Card 
                key={annonce.id}
                className="bg-zinc-900/50 border-zinc-800 overflow-hidden card-hover animate-fade-in"
                style={{ animationDelay: `${idx * 0.05}s` }}
                data-testid={`annonce-${annonce.id}`}
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={annonce.image} 
                    alt={annonce.title}
                    className="w-full h-full object-cover"
                  />
                  {annonce.certified && (
                    <Badge className="absolute top-2 left-2 bg-green-600 text-white">
                      Certifié Le Hub
                    </Badge>
                  )}
                  <div className="absolute top-2 right-2 flex gap-2">
                    <button className="p-2 bg-black/50 rounded-full text-white hover:bg-black/70 transition">
                      <Heart size={16} />
                    </button>
                    <button className="p-2 bg-black/50 rounded-full text-white hover:bg-black/70 transition">
                      <Share2 size={16} />
                    </button>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                    <p className="text-2xl font-bold text-white">{annonce.price.toLocaleString()}€</p>
                  </div>
                </div>
                
                <CardContent className="p-4">
                  <h3 className="text-lg font-bold text-white mb-2">{annonce.title}</h3>
                  
                  {/* Specs */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    <span className="text-xs bg-zinc-800 text-zinc-300 px-2 py-1 rounded flex items-center gap-1">
                      <Calendar size={12} />
                      {annonce.year}
                    </span>
                    <span className="text-xs bg-zinc-800 text-zinc-300 px-2 py-1 rounded flex items-center gap-1">
                      <Gauge size={12} />
                      {annonce.km.toLocaleString()} km
                    </span>
                    <span className="text-xs bg-zinc-800 text-zinc-300 px-2 py-1 rounded flex items-center gap-1">
                      <Fuel size={12} />
                      {annonce.fuel}
                    </span>
                    <span className="text-xs bg-zinc-800 text-zinc-300 px-2 py-1 rounded flex items-center gap-1">
                      <Settings size={12} />
                      {annonce.transmission}
                    </span>
                  </div>
                  
                  <p className="text-zinc-400 text-sm mb-4 line-clamp-2">{annonce.description}</p>
                  
                  {/* Features */}
                  <div className="flex flex-wrap gap-1 mb-4">
                    {annonce.features.slice(0, 3).map((feature, i) => (
                      <span key={i} className="text-xs text-zinc-500 bg-zinc-800/50 px-2 py-0.5 rounded">
                        {feature}
                      </span>
                    ))}
                    {annonce.features.length > 3 && (
                      <span className="text-xs text-zinc-500">+{annonce.features.length - 3}</span>
                    )}
                  </div>
                  
                  {/* Footer */}
                  <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
                    <span className="text-zinc-500 text-xs flex items-center gap-1">
                      <Eye size={14} />
                      {annonce.views} vues
                    </span>
                    <Link to="/contact">
                      <Button size="sm" className="bg-red-600 hover:bg-red-700">
                        Nous contacter
                        <ChevronRight size={14} className="ml-1" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredAnnonces.length === 0 && (
            <div className="text-center py-16">
              <Car className="mx-auto text-zinc-700 mb-4" size={64} />
              <h3 className="text-xl font-bold text-white mb-2">Aucune annonce trouvée</h3>
              <p className="text-zinc-400">Essayez de modifier vos critères de recherche</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-zinc-900/50 border-t border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">VOUS CHERCHEZ UN VÉHICULE PARTICULIER ?</h2>
          <p className="text-zinc-400 mb-8">
            Contactez-nous avec vos critères, nous vous trouverons le véhicule idéal
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/contact">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 btn-glow">
                <Phone size={18} className="mr-2" />
                Nous contacter
              </Button>
            </Link>
            <a href="tel:0805032032">
              <Button size="lg" variant="outline" className="border-zinc-600 text-white hover:bg-zinc-800">
                Appeler le 0805 032 032
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-12 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6 text-center">
            <div>
              <div className="w-14 h-14 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <Settings className="text-green-500" size={28} />
              </div>
              <h3 className="text-white font-bold mb-2">Révisés par nos experts</h3>
              <p className="text-zinc-400 text-sm">Chaque véhicule est contrôlé et révisé dans notre atelier</p>
            </div>
            <div>
              <div className="w-14 h-14 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <Car className="text-blue-500" size={28} />
              </div>
              <h3 className="text-white font-bold mb-2">Garantie incluse</h3>
              <p className="text-zinc-400 text-sm">Tous nos véhicules sont vendus avec une garantie minimum 3 mois</p>
            </div>
            <div>
              <div className="w-14 h-14 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <MapPin className="text-red-500" size={28} />
              </div>
              <h3 className="text-white font-bold mb-2">Sur place à Paris 16e</h3>
              <p className="text-zinc-400 text-sm">Venez essayer les véhicules directement dans notre garage</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
